/**
 * Build a 7-day System Health series from already-fetched period data
 * (orders list, fuel daily_transactions, driver violations, GPS coverage)
 * so charts do not wait for calendar days of live snapshots.
 */

import { classifyOrderStatus } from './orderStatusBuckets';

export type SystemHealthDayScores = {
  date: string;
  telematics: number;
  orders: number;
  drivers: number;
  fuel: number;
};

export type TelematicsActivityDay = {
  date: string;
  positions: number;
  active_vehicles: number;
};

export type OrdersActivityDay = {
  date: string;
  orders: number;
  completed: number;
  cancelled: number;
  open?: number;
};

function clampScore(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

/** Last N UTC calendar dates ending today (oldest → newest). */
export function lastNDateKeys(n: number, end = new Date()): string[] {
  const keys: string[] = [];
  const y = end.getUTCFullYear();
  const m = end.getUTCMonth();
  const d = end.getUTCDate();
  for (let i = n - 1; i >= 0; i--) {
    keys.push(new Date(Date.UTC(y, m, d - i)).toISOString().slice(0, 10));
  }
  return keys;
}

export function orderHealthScoreFromCounts(
  total: number,
  completed: number,
  cancelled: number,
): number {
  if (!total || total <= 0) return 0;
  const completionRate = (completed / total) * 100;
  const cancellationRate = (cancelled / total) * 100;

  let score = 55;
  if (completionRate >= 90) score += 30;
  else if (completionRate >= 80) score += 25;
  else if (completionRate >= 70) score += 20;
  else if (completionRate >= 60) score += 15;
  else if (completionRate > 0) score += completionRate * 0.2;

  if (cancellationRate <= 5) score += 10;
  else if (cancellationRate <= 10) score += 5;
  else score -= cancellationRate * 0.5;

  if (total >= 10000) score += 5;
  else if (total >= 5000) score += 3;

  return clampScore(Math.max(50, Math.min(100, score)));
}

/**
 * Historical telematics score from GPS reporting coverage that day.
 * Live Control Centre / Telematics cards use telematicsAvailabilityScore (alerts + missing GPS).
 */
export function telematicsCoverageScore(
  activeVehicles: number,
  fleetSize: number,
  positions = 0,
): number {
  if (activeVehicles <= 0 && positions <= 0) return 0;
  const denom = Math.max(fleetSize, activeVehicles, 1);
  const coverage = Math.min(1, activeVehicles / denom);
  // Light bonus when the fleet is actively reporting (many pings), capped.
  const volumeBonus = positions > 0 ? Math.min(8, Math.log10(positions + 1) * 3) : 0;
  return clampScore(coverage * 92 + volumeBonus);
}

/** Minimal order shape used for per-day bucketing (payloads vary by source). */
export interface OrderDateSource {
  date?: string | null;
  status?: string | null;
  delivery?: { date?: string | null } | null;
  pickup?: { date?: string | null } | null;
  created?: { date?: string | null } | null;
  createdAt?: string | null;
}

function orderDateKey(o: OrderDateSource): string | null {
  const dateStr = o?.date || o?.delivery?.date || o?.pickup?.date || o?.created?.date || o?.createdAt;
  if (!dateStr) return null;
  const key = String(dateStr).replace(' ', 'T').split('T')[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(key) ? key : null;
}

/**
 * Per-day scores from fetched lists. Missing day activity falls back to
 * the live period scores so weekends/gaps stay readable (except telematics
 * coverage days, which use 0 when no GPS was recorded).
 */
export function buildHealthTrendFromFetchedData(input: {
  days?: number;
  /** Anchor date for the trailing N-day window; defaults to now. Exposed so
   *  tests can pin "today" instead of depending on the real wall clock. */
  end?: Date;
  live: SystemHealthDayScores;
  orders?: OrderDateSource[] | null;
  /** Iceberg order volume by day — preferred over full list for historical scores */
  ordersActivity?: OrdersActivityDay[] | null;
  dailyFuelTransactions?: Record<string, number> | null;
  driverDetails?: Array<{ violations?: Array<{ date?: string }> }> | null;
  driverCount?: number;
  /** Iceberg GPS activity by day — drives historical telematics scores */
  telematicsActivity?: TelematicsActivityDay[] | null;
  telematicsFleetSize?: number | null;
}): SystemHealthDayScores[] {
  const n = input.days ?? 7;
  const keys = input.end ? lastNDateKeys(n, input.end) : lastNDateKeys(n);
  const live = input.live;

  const ordersByDay = new Map<string, { total: number; completed: number; cancelled: number }>();
  for (const row of input.ordersActivity ?? []) {
    if (!row?.date || !/^\d{4}-\d{2}-\d{2}$/.test(row.date)) continue;
    ordersByDay.set(row.date, {
      total: Number(row.orders) || 0,
      completed: Number(row.completed) || 0,
      cancelled: Number(row.cancelled) || 0,
    });
  }
  // Fall back to client-bucketed list only for days the rollup did not cover.
  // Snapshot rollup-covered keys BEFORE this loop: ordersByDay.has(key) would
  // otherwise also be true for a day this same loop already touched (it
  // writes into the same map), so every order after the first one on a given
  // day would be silently skipped instead of accumulated.
  const rollupCoveredDays = new Set(ordersByDay.keys());
  for (const o of input.orders ?? []) {
    const key = orderDateKey(o);
    if (!key || rollupCoveredDays.has(key)) continue;
    const bucket = ordersByDay.get(key) ?? { total: 0, completed: 0, cancelled: 0 };
    bucket.total += 1;
    const status = classifyOrderStatus(o?.status);
    if (status === 'completed') bucket.completed += 1;
    else if (status === 'cancelled') bucket.cancelled += 1;
    ordersByDay.set(key, bucket);
  }

  const fuelByDay = new Map<string, number>();
  let fuelSum = 0;
  let fuelDays = 0;
  for (const [raw, count] of Object.entries(input.dailyFuelTransactions ?? {})) {
    const key = String(raw).replace(' ', 'T').split('T')[0];
    if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) continue;
    const nCount = Number(count) || 0;
    fuelByDay.set(key, nCount);
    if (keys.includes(key)) {
      fuelSum += nCount;
      fuelDays += 1;
    }
  }
  const avgFuelTx = fuelDays > 0 ? fuelSum / fuelDays : 0;

  const driverCount =
    input.driverCount
    ?? (Array.isArray(input.driverDetails) ? input.driverDetails.length : 0);
  const driversWithViolationByDay = new Map<string, Set<number>>();
  (input.driverDetails ?? []).forEach((driver, idx) => {
    for (const v of driver.violations ?? []) {
      if (!v?.date) continue;
      const key = String(v.date).replace(' ', 'T').split('T')[0];
      if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) continue;
      const set = driversWithViolationByDay.get(key) ?? new Set<number>();
      set.add(idx);
      driversWithViolationByDay.set(key, set);
    }
  });

  const telematicsByDay = new Map<string, TelematicsActivityDay>();
  let maxActive = 0;
  let hasAnyTelematicsActivity = false;
  for (const row of input.telematicsActivity ?? []) {
    if (!row?.date || !/^\d{4}-\d{2}-\d{2}$/.test(row.date)) continue;
    telematicsByDay.set(row.date, row);
    maxActive = Math.max(maxActive, Number(row.active_vehicles) || 0);
    if ((row.positions ?? 0) > 0 || (row.active_vehicles ?? 0) > 0) {
      hasAnyTelematicsActivity = true;
    }
  }
  const fleetSize = Math.max(
    Number(input.telematicsFleetSize) || 0,
    maxActive,
  );

  return keys.map((date) => {
    if (date === live.date) return { ...live };

    let orders = live.orders;
    const dayOrders = ordersByDay.get(date);
    const hasOrdersRollup = (input.ordersActivity?.length ?? 0) > 0;
    if (hasOrdersRollup && ordersByDay.has(date)) {
      // Explicit Iceberg day (including zero) — do not flatten to today's live score
      orders =
        dayOrders && dayOrders.total > 0
          ? orderHealthScoreFromCounts(dayOrders.total, dayOrders.completed, dayOrders.cancelled)
          : 0;
    } else if (dayOrders && dayOrders.total > 0) {
      orders = orderHealthScoreFromCounts(dayOrders.total, dayOrders.completed, dayOrders.cancelled);
    }

    let drivers = live.drivers;
    if (driverCount > 0 && driversWithViolationByDay.has(date)) {
      const withViolations = driversWithViolationByDay.get(date)?.size ?? 0;
      // Same definition as Control Centre: % of drivers with no violation that day.
      drivers = Math.round(((driverCount - withViolations) / driverCount) * 100);
    }

    let fuel = live.fuel;
    if (avgFuelTx > 0 && fuelByDay.has(date) && live.fuel > 0) {
      const dayTx = fuelByDay.get(date) ?? 0;
      const relative = dayTx / avgFuelTx;
      fuel = clampScore(live.fuel * (0.82 + 0.18 * Math.min(1.4, relative)));
    } else if (fuelByDay.has(date) && live.fuel <= 0) {
      const dayTx = fuelByDay.get(date) ?? 0;
      fuel = clampScore(55 + Math.min(35, dayTx * 1.5));
    }

    let telematics = live.telematics;
    if (hasAnyTelematicsActivity) {
      const dayGps = telematicsByDay.get(date);
      telematics = telematicsCoverageScore(
        dayGps?.active_vehicles ?? 0,
        fleetSize,
        dayGps?.positions ?? 0,
      );
    }

    return {
      date,
      telematics,
      orders,
      drivers,
      fuel,
    };
  });
}
